import { format } from "date-fns";

import { SessionObject } from "@/components/objects-list/constants";
import { EmptyPageInfo, type PageInfo, type ResponseData } from "@/hooks";
import { getPNGFromImgBlob } from "@/utils/images";

import { File, SortingInput } from "../../components/graphql/schema.graphql";
import { isSVGMimeType } from "../../utils/mimetype";
import { getConnectionNodesTyped } from "../../utils/relay";
import {
  DEFAULT_FORGE_SESSION_NAME,
  FilterTabType,
  ObjectTabType,
} from "./constants";
import { GetSessionObjectsQueryResult } from "./forge-session/queries.graphql";

const getObjectTypeByObjectTabType = (objectTab: ObjectTabType) => {
  return { file: "FILE", asset: "ASSET", all: undefined }[objectTab];
};

export const getForgeSessionNameFromGenerationType = (
  generationType: string,
) => {
  // Make the name based on the date and generation type
  const date = new Date();
  return `${format(date, "MMM dd")}, ${generationType}`;
};

export const getForgeSessionNameFromPrompt = (prompt?: string | null) => {
  if (!prompt) {
    return DEFAULT_FORGE_SESSION_NAME;
  }

  const MAX_CHARS = 30;

  const tokens = prompt.split(" ");
  let sessionName = tokens[0] || "";

  if (sessionName.length > MAX_CHARS) {
    return sessionName.slice(0, MAX_CHARS);
  }

  for (let i = 1; i < tokens.length; i++) {
    if ((sessionName + " " + tokens[i]).length <= MAX_CHARS) {
      sessionName += " " + tokens[i];
    } else {
      break;
    }
  }

  return sessionName;
};

export const getFilteringInputs = (
  searchText: string,
  filterTab: FilterTabType,
  objectTab: ObjectTabType,
) => {
  const filters = [];
  if (searchText?.length) {
    filters.push({
      field: "fulltext",
      operator: "search",
      value: searchText,
    });
  }
  if (filterTab === "favorite") {
    filters.push({
      field: "is_liked",
      operator: "eq",
      value: "true",
    });
  }
  if (objectTab !== "all") {
    filters.push({
      field: "type",
      operator: "in",
      value: JSON.stringify([getObjectTypeByObjectTabType(objectTab)]),
    });
  }
  return filters;
};

export const getSortingInputs = (searchText: string): SortingInput[] =>
  searchText?.length
    ? [
        {
          field: "rank",
          direction: "DESC",
        },
      ]
    : [];

export const extractQueryItems = ({
  session,
}: ResponseData<GetSessionObjectsQueryResult>): {
  pageInfo: PageInfo;
  items: SessionObject[];
} => {
  if (!session?.objects) {
    return {
      pageInfo: EmptyPageInfo,
      items: [],
    };
  }
  return {
    // TODO: PageInfo doesn't match the interface we use in the codebase. We should fix this.
    pageInfo: {
      hasNextPage: session.objects.pageInfo.hasNextPage ?? false,
      hasPreviousPage: session.objects.pageInfo.hasPreviousPage ?? false,
      startCursor: session.objects.pageInfo.startCursor ?? undefined,
      endCursor: session.objects.pageInfo.endCursor ?? undefined,
    },
    items: getConnectionNodesTyped(session.objects).filter(
      (node): node is SessionObject =>
        node.__typename === "File" || node.__typename === "Asset",
    ),
  };
};

export const INVALID_ITEM_TYPE_ERR =
  "This type of item cannot be copied to the clipboard";

export const copyImages = async (
  images: Pick<File, "url" | "contentType">[],
) => {
  const clipboardItems = await Promise.all(
    images.map(async ({ url, contentType }) => {
      if (contentType !== "" && !contentType?.startsWith("image/")) {
        console.error(
          `Attempted to copy item of type ${contentType}, but it is not a supported type`,
        );
        throw new Error(INVALID_ITEM_TYPE_ERR);
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch the file from ${url}`);

      // Content-Type may not be set. If not, we rely on the response's HTTP header
      if (!contentType) {
        const maybeContentType = response.headers.get("Content-Type");

        if (!maybeContentType) {
          console.error("Unable to determine item content type");
          throw new Error(INVALID_ITEM_TYPE_ERR);
        }

        contentType = maybeContentType;
        if (!contentType.startsWith("image/")) {
          console.error(
            `Attempted to copy item of type ${contentType}, but it is not a supported type`,
          );
          throw new Error(INVALID_ITEM_TYPE_ERR);
        }
      }

      let fileBlob = await response.blob();
      const mimeType = fileBlob.type;

      // SVGs are textual. They should not be copied as a binary blob, but rather as plain text
      if (isSVGMimeType(mimeType)) {
        const content = await fileBlob.text();
        return new ClipboardItem({ "text/plain": content });
      }

      if (contentType.startsWith("image/") && mimeType !== "image/png") {
        fileBlob = await getPNGFromImgBlob(fileBlob);
      }

      return new ClipboardItem({ [fileBlob.type]: fileBlob });
    }),
  );

  await navigator.clipboard.write(clipboardItems);
};
